<template>
    <div data-test-id="l-reviews-edit-confirm-form">
        <div class="clickable" tabindex="0" @click="goTo('basics')" @keyup.enter="goTo('basics')">
            <i
                class="fa"
                :class="{
                    'fa-circle-o': !reviewCycleSetupProgress.name,
                    'fa-check-circle': reviewCycleSetupProgress.name,
                }"
            ></i>
            <span class="l-default-margin-s">{{ $t(`reviews.admin.labelDefineBasics`) }}</span>
        </div>
        <div
            v-show="!isTemplate"
            class="clickable"
            tabindex="0"
            @click="goTo('participants')"
            @keyup.enter="goTo('participants')"
        >
            <i
                class="fa"
                :class="{
                    'fa-circle-o': !reviewCycleSetupProgress.participants,
                    'fa-check-circle': reviewCycleSetupProgress.participants,
                }"
            ></i>
            <span class="l-default-margin-s">{{
                $t(`reviews.admin.labelDefineParticipants`)
            }}</span>
        </div>
        <div class="clickable" tabindex="0" @click="goTo('scope')" @keyup.enter="goTo('scope')">
            <i
                class="fa"
                :class="{
                    'fa-circle-o': !reviewCycleSetupProgress.scope,
                    'fa-check-circle': reviewCycleSetupProgress.scope,
                }"
            ></i>
            <span class="l-default-margin-s">{{ $t(`reviews.admin.labelDefineScope`) }}</span>
        </div>
        <div
            class="clickable"
            tabindex="0"
            @click="goTo('questions')"
            @keyup.enter="goTo('questions')"
        >
            <i
                class="fa"
                :class="{
                    'fa-circle-o': !reviewCycleSetupProgress.questions,
                    'fa-check-circle': reviewCycleSetupProgress.questions,
                }"
            ></i>
            <span class="l-default-margin-s">{{ $t(`reviews.admin.labelDefineQuestions`) }}</span>
        </div>
        <div
            class="clickable"
            tabindex="0"
            @click="goTo('visibility')"
            @keyup.enter="goTo('visibility')"
        >
            <i
                class="fa"
                :class="{
                    'fa-circle-o': !reviewCycleSetupProgress.visibility,
                    'fa-check-circle': reviewCycleSetupProgress.visibility,
                }"
            ></i>
            <span class="l-default-margin-s">{{ $t(`reviews.admin.labelDefineVisibility`) }}</span>
        </div>
        <div
            class="clickable"
            tabindex="0"
            @click="goTo('timeline')"
            @keyup.enter="goTo('timeline')"
        >
            <i
                class="fa"
                :class="{
                    'fa-circle-o': !reviewCycleSetupProgress.timeline,
                    'fa-check-circle': reviewCycleSetupProgress.timeline,
                }"
            ></i>
            <span class="l-default-margin-s">{{ $t(`reviews.admin.labelDefineTimeline`) }}</span>
        </div>
        <div
            class="clickable"
            tabindex="0"
            @click="goTo('notifications')"
            @keyup.enter="goTo('notifications')"
        >
            <i
                class="fa"
                :class="{
                    'fa-circle-o': !reviewCycleSetupProgress.notifications,
                    'fa-check-circle': reviewCycleSetupProgress.notifications,
                }"
            ></i>
            <span class="l-default-margin-s">{{
                $t(`reviews.admin.labelDefineNotifications`)
            }}</span>
        </div>

        <hr />

        <div v-show="errorKickoff" class="alert alert-light alert-danger">
            {{ errorKickoff }}
        </div>

        <div class="row">
            <div class="col-md-3 col-md-offset-9">
                <button
                    class="btn btn-primary btn-raised btn-block"
                    :disabled="isButtonDisabled"
                    tabindex="0"
                    @click="
                        isTemplate
                            ? updateStatus('templateActive', true)
                            : updateStatus('new', true)
                    "
                >
                    {{ $t(`reviews.admin.btnConfirmSetup`) }}
                </button>
            </div>
        </div>

        <!-- Congrats Modal -->
        <l-modal-congrats
            v-show="showCongratsModal"
            :title="$t(`reviews.admin.createCongratsModalTitle`)"
            :text="
                new Date(reviewCycle.status.startPlan) > new Date()
                    ? $t(`reviews.admin.createCongratsModalText`)
                    : $t(`reviews.admin.createCongratsModalTextKickoffImminent`)
            "
            :btn-text="$t(`reviews.admin.createCongratsModalBtnText`)"
            :image="congratsModalImage"
            @next="
                () => {
                    showCongratsModal = false;
                    $router.push(`/reviews/cycles/edit/${this.id}/status`);
                }
            "
        />
        <!--/Congrats Modal -->
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import LModalCongrats from "@/components/LModalCongrats";

import congratsModalImage from "@/img/icons/progress.png";

export default {
    components: {
        LModalCongrats,
    },
    props: {
        id: {
            type: String,
            required: true,
        },
        isTemplate: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            reviewCycle: {},

            updateStatusNewStatus: "",
            updateStatusCheck: false,
            updatingStatus: false,
            errorKickoff: null,

            showCongratsModal: false,
            congratsModalImage,
        };
    },
    computed: {
        ...mapGetters({
            getTemplateDetails: "reviews/templates/details",
            getCycleDetails: "reviews/cycles/details",
        }),

        reviewCycleSetupProgress() {
            const name = Boolean(this.reviewCycle.name);
            const participants = Boolean(
                this.reviewCycle.participationRules?.include?.all ||
                    this.reviewCycle.participationRules?.include?.users?.length ||
                    this.reviewCycle.participationRules?.include?.locations?.length ||
                    this.reviewCycle.participationRules?.include?.localTeams?.length,
            );
            const scope = Boolean(
                this.reviewCycle.selfEvaluationsEnabled ||
                    this.reviewCycle.peerReviewsEnabled ||
                    this.reviewCycle.directReportReviewsEnabled ||
                    this.reviewCycle.managerReviewsEnabled,
            );
            const questions = Boolean(this.reviewCycle.questions?.skills?.length);
            const visibility = Boolean(this.reviewCycle.visibility);
            const timeline = Boolean(
                this.reviewCycle.status?.peerNominationsDelay ||
                    this.reviewCycle.status?.peerApprovalDelay ||
                    this.reviewCycle.status?.peerReviewsDelay ||
                    this.reviewCycle.status?.managerReviewsDelay ||
                    this.reviewCycle.status?.conversationsDelay,
            );
            const notifications = Boolean(this.reviewCycle.customNotifications);

            return {
                name,
                participants,
                scope,
                questions,
                visibility,
                timeline,
                notifications,
            };
        },

        isButtonDisabled() {
            if (this.isTemplate) {
                return (
                    !this.reviewCycleSetupProgress.name ||
                    !this.reviewCycleSetupProgress.scope ||
                    !this.reviewCycleSetupProgress.questions ||
                    !this.reviewCycleSetupProgress.visibility ||
                    !this.reviewCycleSetupProgress.timeline
                );
            }
            return (
                !this.reviewCycleSetupProgress.name ||
                !this.reviewCycleSetupProgress.participants ||
                !this.reviewCycleSetupProgress.scope ||
                !this.reviewCycleSetupProgress.questions ||
                !this.reviewCycleSetupProgress.visibility ||
                !this.reviewCycleSetupProgress.timeline
            );
        },
    },
    watch: {
        $route: {
            immediate: true,
            handler() {
                this.reviewCycle = this.isTemplate
                    ? this.getTemplateDetails(this.id)
                    : this.getCycleDetails(this.id);
            },
        },
    },
    methods: {
        goTo(section) {
            if (this.isTemplate) {
                this.$router.push(`/reviews/templates/edit/${this.id}/${section}`);
            } else {
                this.$router.push(`/reviews/cycles/edit/${this.id}/${section}`);
            }
        },
        updateStatus(newStatus, confirmed) {
            // double check before updating

            this.updateStatusNewStatus = newStatus;

            if (typeof confirmed === "undefined" && newStatus != "draft") {
                this.updateStatusCheck = true;
                return;
            } else if (!confirmed && newStatus != "draft") {
                this.updateStatusCheck = false;
                return;
            } else {
                this.updateStatusCheck = false;
            }

            // set flag

            this.updatingStatus = true;

            // update

            if (this.isTemplate) {
                this.$store
                    .dispatch({
                        type: "reviews/templates/updateStatus",
                        payload: { id: this.id, status: this.updateStatusNewStatus },
                    })
                    .then(() => {
                        // move to confirmation view

                        this.$router.push("/reviews/templates");

                        this.errorKickoff = null;

                        // reset flag

                        this.updatingStatus = false;
                    })
                    .catch(err => {
                        // show error

                        this.errorKickoff = err?.response?.data?.error;
                    });
            } else {
                this.$store
                    .dispatch({
                        type: "reviews/cycles/updateStatus",
                        payload: { id: this.id, status: this.updateStatusNewStatus },
                    })
                    .then(() => {
                        // move to confirmation view

                        this.showCongratsModal = true;

                        this.errorKickoff = null;

                        // reset flag

                        this.updatingStatus = false;
                    })
                    .catch(err => {
                        // show error

                        this.errorKickoff = err?.response?.data?.error;
                    });
            }
        },
    },
};
</script>
